import { supabase } from '@/integrations/supabase/client';

// Type assertion for achievements table until migration is applied
declare module '@supabase/supabase-js' {
  interface Database {
    public: {
      Tables: {
        achievements: {
          Row: {
            id: string;
            user_id: string;
            badge_name: string;
            badge_type: string;
            description: string;
            icon: string | null;
            earned_at: string;
            metadata: Record<string, any> | null;
            created_at: string;
            updated_at: string;
          };
          Insert: {
            id?: string;
            user_id: string;
            badge_name: string;
            badge_type: string;
            description: string;
            icon?: string | null;
            earned_at?: string;
            metadata?: Record<string, any> | null;
            created_at?: string;
            updated_at?: string;
          };
          Update: {
            id?: string;
            user_id?: string;
            badge_name?: string;
            badge_type?: string;
            description?: string;
            icon?: string | null;
            earned_at?: string;
            metadata?: Record<string, any> | null;
            created_at?: string;
            updated_at?: string;
          };
        };
      };
      Functions: {
        award_badge: {
          Args: {
            p_user_id: string;
            p_badge_name: string;
            p_badge_type: string;
            p_description: string;
            p_icon?: string;
            p_metadata?: Record<string, any>;
          };
          Returns: boolean;
        };
        check_and_award_streak_badges: {
          Args: {
            p_user_id: string;
          };
          Returns: void;
        };
      };
    };
  }
}

export interface Achievement {
  id: string;
  user_id: string;
  badge_name: string;
  badge_type: 'milestone' | 'streak' | 'activity' | 'special';
  description: string;
  icon?: string;
  earned_at: string;
  metadata?: Record<string, any>;
}

export interface BadgeDefinition {
  name: string;
  type: 'milestone' | 'streak' | 'activity' | 'special';
  description: string;
  icon: string;
  condition: (user: any, data?: any) => boolean;
  metadata?: Record<string, any>;
}

// Define all available badges
export const BADGE_DEFINITIONS: BadgeDefinition[] = [
  // Milestone badges
  {
    name: 'welcome_aboard',
    type: 'milestone',
    description: 'Welcome to 30DayCashSprint!',
    icon: '🎉',
    condition: () => true, // Awarded on signup
  },
  {
    name: 'first_idea_validator',
    type: 'milestone',
    description: 'Validated your first startup idea!',
    icon: '💡',
    condition: () => true, // Awarded on first validation
  },
  {
    name: 'journal_writer',
    type: 'milestone',
    description: 'Wrote your first journal entry!',
    icon: '📝',
    condition: () => true, // Awarded on first journal entry
  },
  
  // Streak badges (awarded by database function)
  {
    name: 'first_day',
    type: 'streak',
    description: 'Completed your first day!',
    icon: '🎯',
    condition: () => false, // Handled by database
  },
  {
    name: 'three_day_streak',
    type: 'streak',
    description: 'Three days of consistent work!',
    icon: '🔥',
    condition: () => false, // Handled by database
  },
  {
    name: 'week_warrior',
    type: 'streak',
    description: 'One week of dedication!',
    icon: '⚡',
    condition: () => false, // Handled by database
  },
  {
    name: 'two_week_champion',
    type: 'streak',
    description: 'Two weeks of building!',
    icon: '🏆',
    condition: () => false, // Handled by database
  },
  {
    name: 'month_master',
    type: 'streak',
    description: 'Completed the 30-day challenge!',
    icon: '👑',
    condition: () => false, // Handled by database
  },
];

class AchievementService {
  // Award a badge to a user
  async awardBadge(
    userId: string,
    badgeName: string,
    metadata?: Record<string, any>
  ): Promise<boolean> {
    try {
      const badgeDefinition = BADGE_DEFINITIONS.find(b => b.name === badgeName);
      if (!badgeDefinition) {
        console.warn(`Badge definition not found: ${badgeName}`);
        return false;
      }

      const { data, error } = await (supabase as any).rpc('award_badge', {
        p_user_id: userId,
        p_badge_name: badgeName,
        p_badge_type: badgeDefinition.type,
        p_description: badgeDefinition.description,
        p_icon: badgeDefinition.icon,
        p_metadata: metadata || badgeDefinition.metadata || {}
      });

      if (error) {
        console.error('Error awarding badge:', error);
        return false;
      }

      return data;
    } catch (error) {
      console.error('Error in awardBadge:', error);
      return false;
    }
  }

  // Get all achievements for a user
  async getUserAchievements(userId: string): Promise<Achievement[]> {
    try {
      const { data, error } = await (supabase as any)
        .from('achievements')
        .select('*')
        .eq('user_id', userId)
        .order('earned_at', { ascending: false });

      if (error) {
        console.error('Error fetching achievements:', error);
        return [];
      }

      return (data || []) as Achievement[];
    } catch (error) {
      console.error('Error in getUserAchievements:', error);
      return [];
    }
  }

  // Check and award streak badges
  async checkStreakBadges(userId: string): Promise<void> {
    try {
      await (supabase as any).rpc('check_and_award_streak_badges', {
        p_user_id: userId
      });
    } catch (error) {
      console.error('Error checking streak badges:', error);
    }
  }

  // Award welcome badge on signup
  async awardWelcomeBadge(userId: string): Promise<void> {
    const awarded = await this.awardBadge(userId, 'welcome_aboard');
    if (awarded) {
      this.showBadgeNotification('welcome_aboard');
    }
  }

  // Award idea validation badge
  async awardIdeaValidationBadge(userId: string): Promise<void> {
    const awarded = await this.awardBadge(userId, 'first_idea_validator');
    if (awarded) {
      this.showBadgeNotification('first_idea_validator');
    }
  }

  // Award journal entry badge
  async awardJournalBadge(userId: string): Promise<void> {
    const awarded = await this.awardBadge(userId, 'journal_writer');
    if (awarded) {
      this.showBadgeNotification('journal_writer');
    }
  }

  // Show badge notification
  private showBadgeNotification(badgeName: string): void {
    const badge = BADGE_DEFINITIONS.find(b => b.name === badgeName);
    if (badge) {
      // Dispatch custom event for badge notification
      const event = new CustomEvent('badge-earned', {
        detail: {
          badge: badge,
          message: `Badge earned: ${badge.description}!`
        }
      });
      window.dispatchEvent(event);
    }
  }

  // Get badge count for pod matching
  async getBadgeCount(userId: string): Promise<number> {
    try {
      const { count, error } = await (supabase as any)
        .from('achievements')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        console.error('Error getting badge count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getBadgeCount:', error);
      return 0;
    }
  }
}

export const achievementService = new AchievementService();
